/**
 * ThreatDetailPane — expansion pane that drops below a Threat row
 * when the user clicks its chevron. Verbatim port of the legacy
 * `ThreatTraceabilityPage.jsx`'s detail content, restructured to
 * use centralised colour + label accessors (no hex / no inline
 * capitalisation) and to live inside the Chain column's cell so
 * we don't have to extend DataTable with row-spanning support.
 *
 * Level badges (impact / feasibility / risk value / treatment /
 * CAL) render through the shared `<LevelBadge>` primitive with
 * `size="sm"` — matches the TARA workflow's badge styling
 * (filled chip with auto-contrast text). Each badge sits
 * immediately to the right of the item's id, before the
 * descriptive text.
 *
 * All linked entities arrive pre-resolved on the `row` prop
 * (built by `toChainRows` in Step 4). Sections render only when
 * non-empty.
 */
import React from 'react';
import LevelBadge from '../../../components/ui/LevelBadge';
import CategoryPill from '../../../components/ui/CategoryPill';
import {
  getImpactColor,
  getImpactLabel,
  getFeasibilityColor,
  getFeasibilityLabel,
  getTreatmentDecisionColor,
  getTreatmentDecisionLabel,
  getRiskValueColor,
  getCALColor,
  getCALLabel,
} from '../../../utils/severityStyle';
import styles from './ThreatDetailPane.module.css';

const Section = ({ title, count, children }) => (
  <div className={styles.section}>
    <div className={styles.sectionHead}>
      <span className={styles.sectionTitle}>{title}</span>
      {count != null && <span className={styles.sectionCount}>{count}</span>}
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </div>
);

const ThreatDetailPane = ({ row }) => {
  const { threat_scenario, compromised_property, asset, damages, attackPaths, risks, goals, claims, controls, requirements } = row;

  return (
    <div className={styles.pane} onClick={(e) => e.stopPropagation()}>
      {threat_scenario && (
        <Section title="Threat scenario">
          <p className={styles.scenarioText}>{threat_scenario}</p>
          {compromised_property && (
            <div className={styles.pillRow}>
              <CategoryPill label={`Compromised: ${compromised_property}`} size="sm" />
            </div>
          )}
        </Section>
      )}

      {asset && (
        <Section title="Asset">
          <div className={styles.assetName}>{asset.asset_name || asset.asset_id}</div>
          {asset.asset_type && (
            <div className={styles.pillRow}>
              <CategoryPill label={asset.asset_type} size="sm" />
            </div>
          )}
        </Section>
      )}

      {damages.length > 0 && (
        <Section title="Damage scenarios" count={damages.length}>
          <ul className={styles.list}>
            {damages.map((d) => (
              <li key={d.damage_id} className={styles.listItem}>
                <span className={styles.itemId}>{d.damage_id}</span>
                {d.final_impact_rating && (
                  <LevelBadge
                    color={getImpactColor(d.final_impact_rating)}
                    label={getImpactLabel(d.final_impact_rating)}
                    size="sm"
                  />
                )}
                <span className={styles.itemText}>{d.damage_scenario}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {attackPaths.length > 0 && (
        <Section title="Attack paths" count={attackPaths.length}>
          <ul className={styles.list}>
            {attackPaths.map((ap) => (
              <li key={ap.attack_path_id} className={styles.listItem}>
                <span className={styles.itemId}>{ap.attack_path_id}</span>
                {ap.attack_feasibility_rating && (
                  <LevelBadge
                    color={getFeasibilityColor(ap.attack_feasibility_rating)}
                    label={getFeasibilityLabel(ap.attack_feasibility_rating)}
                    size="sm"
                  />
                )}
                <span className={styles.itemText}>{ap.attack_path}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {risks.length > 0 && (
        <Section title="Risks" count={risks.length}>
          <ul className={styles.list}>
            {risks.map((r) => (
              <li key={r.risk_id} className={styles.listItem}>
                <span className={styles.itemId}>{r.risk_id}</span>
                {r.risk_value != null && (
                  <LevelBadge
                    color={getRiskValueColor(r.risk_value)}
                    label={String(r.risk_value)}
                    size="sm"
                  />
                )}
                {r.treatment_decision && (
                  <LevelBadge
                    color={getTreatmentDecisionColor(r.treatment_decision)}
                    label={getTreatmentDecisionLabel(r.treatment_decision)}
                    size="sm"
                  />
                )}
                {r.treatment_justification && (
                  <span className={styles.itemText}>{r.treatment_justification}</span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {goals.length > 0 && (
        <Section title="Cybersecurity goals" count={goals.length}>
          <ul className={styles.list}>
            {goals.map((g) => (
              <li key={g.goal_id} className={styles.listItem}>
                <span className={styles.itemId}>{g.goal_id}</span>
                <span className={styles.itemText}>{g.goal_statement}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {claims.length > 0 && (
        <Section title="Cybersecurity claims" count={claims.length}>
          <ul className={styles.list}>
            {claims.map((c) => (
              <li key={c.claim_id} className={styles.listItem}>
                <span className={styles.itemId}>{c.claim_id}</span>
                <span className={styles.itemText}>{c.claim_statement}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {controls.length > 0 && (
        <Section title="Controls" count={controls.length}>
          <ul className={styles.list}>
            {controls.map((c) => (
              <li key={c.control_id} className={styles.listItem}>
                <span className={styles.itemId}>{c.control_id}</span>
                <span className={styles.itemText}>{c.control_name}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {requirements.length > 0 && (
        <Section title="Requirements" count={requirements.length}>
          <ul className={styles.list}>
            {requirements.map((req) => (
              <li key={req.req_id} className={styles.listItem}>
                <span className={styles.itemId}>{req.req_id}</span>
                {req.cal_level && (
                  <LevelBadge
                    color={getCALColor(req.cal_level)}
                    label={getCALLabel(req.cal_level)}
                    size="sm"
                  />
                )}
                <span className={styles.itemText}>
                  {req.req_statement || req.requirement_text}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

export default ThreatDetailPane;
